import React, { useState } from 'react';
import { supabase } from '../services/supabase';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import Button from '../components/shared/Button';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setError(error.message);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(t('auth.login_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/confirm`,
      });

      if (error) {
        setError(error.message);
      } else {
        showNotification(t('auth.reset_password_sent'), 'success');
        setIsLogin(true);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(t('auth.reset_password_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {isLogin ? t('common.login') : t('auth.forgot_password')}
          </h2>
        </div>

        <form className="mt-8 space-y-6" onSubmit={isLogin ? handleLogin : handleForgotPassword}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('common.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder={t('common.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {isLogin && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('common.password')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="mt-1 relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder={t('common.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}
          </div>

          {error && <div className="text-red-600 text-sm mt-2">{error}</div>}

          <div>
            <Button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              disabled={loading}
            >
              {loading
                ? t('common.loading')
                : isLogin
                  ? t('common.login')
                  : t('auth.send_reset_link')}
            </Button>
          </div>

          {isLogin && (
            <div className="text-center">
              <button
                type="button"
                className="text-indigo-600 hover:text-indigo-500 text-sm"
                onClick={() => setIsLogin(false)}
              >
                {t('auth.forgot_password')}
              </button>
            </div>
          )}

          {!isLogin && (
            <div className="text-center">
              <button
                type="button"
                className="text-indigo-600 hover:text-indigo-500 text-sm"
                onClick={() => setIsLogin(true)}
              >
                {t('auth.back_to_login')}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
